import {Injectable} from '@angular/core';
import {ChartConstants} from '@constants/pages/chart-constants';
import {ChartsHelperService} from '@services/charts/helper/charts-helper.service';
import {Signal} from '@models/dashboard/signal';
import {DatePipe, DecimalPipe} from '@angular/common';
import {TooltipOptions} from 'highcharts';
import { TranslationService } from '@services/translation/translation.service';

@Injectable({
  providedIn: 'root'
})
export class ChartTooltipSettingsGeneratorService {

  constructor(private chartsHelperService: ChartsHelperService,
              private decimalPipe: DecimalPipe,
              private datePipe: DatePipe,
              private translationService: TranslationService) {
  }


  public generateTooltipSettings(chartSettings, machineSignals, stringData, isLineChart: boolean, isInEditMode: boolean, portalLanguage: string, data): TooltipOptions {
    const self = this;
    return {
      shared: true,
      borderColor: ChartConstants.TOOLTIP.BORDER_COLOR,
      xDateFormat: ChartConstants.TOOLTIP.DEFAULT_TIME_FORMAT,
      backgroundColor: ChartConstants.TOOLTIP.BACKGROUND_COLOR,
      borderWidth: ChartConstants.TOOLTIP.BORDER_WIDTH,
      useHTML: true,
      formatter: function () {
        if (isInEditMode) {
          return undefined;
        }

        if (isLineChart) {
          return self.lineWidgetTooltipFormatter(chartSettings, machineSignals, portalLanguage, this, data, isLineChart);
        } else {
          return self.interactiveDataAnalysisTooltipFormatter(chartSettings, machineSignals, stringData, isLineChart, this);
        }
      }
    };
  }

  private lineWidgetTooltipFormatter(chartSettings, machineSignals,portalLanguage, tooltipControl, data, isLineChart) {
    if (chartSettings.isInEditMode) {
      return;
    }

    const pointDate = new Date(tooltipControl.points[0].key);
    const pattern = this.chartsHelperService.getDateTimeFormatPattern(isLineChart, data, portalLanguage, chartSettings);
    let tooltip = '<span>' + this.datePipe.transform(pointDate, pattern, undefined, portalLanguage) + '</span><br/><table>';
    for (const point of tooltipControl.points) {
      const id = Number(point.series.userOptions.id);

      const machineSignal = this.getLineWidgetMachineSignalById(id, machineSignals);
      const chartSignal = this.getLineWidgetSignal(id, chartSettings);

      const decimals = this.chartsHelperService.getSignalPrecision(machineSignal, chartSignal);
      const unit = this.chartsHelperService.getSignalUnitOrFallback(machineSignal, chartSignal);

      const value = this.decimalPipe.transform(point.y, '1.' + decimals + '-' + decimals, this.translationService.getActiveLanguage());
      tooltip += '<tr><td style="white-space:pre; color:' + point.color + '">● </td><td>' +
          point.series.name + ' : </td><td style="white-space:pre"> </td><td style="text-align: right"><b>' +
          value + '</b></td><td style="white-space:pre"> </td><td style="text-align: left"><b>' + unit + '</b></td></tr>';
    }

    tooltip += '</table>';

    return tooltip;

  }


  private interactiveDataAnalysisTooltipFormatter(chartSettings, machineSignals, stringData, isLineChart: boolean, tooltipControl) {
    if (chartSettings.isInEditMode) {
      return;
    }

    const datePattern = this.getDateTimePattern(chartSettings);
    const pointDate = new Date(tooltipControl.points[0].key);
    let tooltip = '<span>' + this.datePipe.transform(pointDate, datePattern == 'MM-dd HH:mm:ss' ? 'medium' : 'short', undefined, this.translationService.getActiveLanguage()) + '</span><br/>';

    tooltip += '<table>';

    let signals = chartSettings.signals;
    if (isLineChart) {
      signals = this.getLineChartSettingsSignals(chartSettings);
    }
    const tooltipContentMap = this.extractSignalDetailsForTooltip(tooltipControl, machineSignals, signals);
    for (const t in tooltipContentMap) {
      if (tooltipContentMap.hasOwnProperty(t)) {
        const signalDetails = tooltipContentMap[t];

        const value = this.decimalPipe.transform(signalDetails.value, '1.' + signalDetails.decimals + '-' + signalDetails.decimals , this.translationService.getActiveLanguage());
        const minValue = this.decimalPipe.transform(signalDetails.min, '1.' + signalDetails.decimals + '-' + signalDetails.decimals, this.translationService.getActiveLanguage());
        const maxValue = this.decimalPipe.transform(signalDetails.max, '1.' + signalDetails.decimals + '-' + signalDetails.decimals, this.translationService.getActiveLanguage());

        tooltip += '<tr>';
        tooltip += '<td style="white-space:pre; color: ' + signalDetails.color + ';"><em class="fas fa-circle"></em> </td>';
        tooltip += '<td> ' + signalDetails.label + ' :</td>';
        tooltip += '<td style="white-space:pre"></td>';
        tooltip += '<td style="text-align: right">';
        if (this.isDefined(signalDetails.value)) {
          tooltip += '<b> ' + value + '</b>';
        }
        if (this.isDefined(signalDetails.min) && this.isDefined(signalDetails.max)) {
          tooltip += '<b> [' + minValue + ' - ' + maxValue + '] </b>';
        }
        tooltip += '</td>';
        tooltip += '<td style="white-space:pre"> </td>';
        tooltip += '<td style="text-align: left;white-space:pre;"> <b> ' + signalDetails.unit + '</b> </td>';
        tooltip += '</tr>';

      }
    }

    if (stringData && stringData.serieName && ChartsHelperService.NO_STRING !== stringData.serieName) {
      tooltip = this.addStringDataToTooltip(tooltip, tooltipControl.x, stringData);
    }

    tooltip += '</table>';
    return tooltip;
  }

  private getLineChartSettingsSignals(chartSettings) {
    const signals: any[] = [];
    for (const axis of chartSettings.axis) {
      for (const signal of axis.signals) {
        signals.push(signal);
      }
    }
    return signals;
  }

  private getDateTimePattern(chartSettings) {
    if (chartSettings?.intervalDetails?.settings?.format) {
      return chartSettings.intervalDetails.settings.format;
    }
    return 'dd hh:mm';
  }

  private extractSignalDetailsForTooltip(tooltipControl, machineSignals, chartSignals) {
    const tooltipContentMap = {};

    for (const currentPoint of tooltipControl.points) {
      const signalId = currentPoint.series.userOptions.id;
      if (!tooltipContentMap.hasOwnProperty(signalId)) {
        tooltipContentMap[signalId] = {};
      }

      if (this.isDefined(currentPoint.point.low) && this.isDefined(currentPoint.point.high)) {
        tooltipContentMap[signalId].min = currentPoint.point.low;
        tooltipContentMap[signalId].max = currentPoint.point.high;
      } else {
        tooltipContentMap[signalId].value = currentPoint.point.y;
      }
      if (!tooltipContentMap[signalId].color) {
        tooltipContentMap[signalId].color = currentPoint.color;
      }

      if (!tooltipContentMap[signalId].decimals) {
        tooltipContentMap[signalId].decimals = this.getSignalPrecision(signalId, machineSignals, chartSignals);
      }
      if (!tooltipContentMap[signalId].unit) {
        tooltipContentMap[signalId].unit = this.getSignalUnit(signalId, machineSignals, chartSignals);
      }

      if (!tooltipContentMap[signalId].label) {
        tooltipContentMap[signalId].label = currentPoint.series.name ? currentPoint.series.name.replace('_range', '') : null;
      }

    }
    return tooltipContentMap;
  }

  private isDefined(value) {
    return typeof value !== 'undefined';
  }

  private addStringDataToTooltip(tooltip, x, stringData) {

    tooltip += '<tr>';
    tooltip += '<td style="white-space:pre; color: #999999;"><em class="fas fa-rectangle-landscape"></em> </td>';
    tooltip += '<td> ' + this.translationService.translate.instant(stringData.displayedText) + ' :</td>';
    tooltip += '<td style="white-space:pre"></td>';
    tooltip += '<td style="text-align: right" colspan="3">';
    const stringValue = this.extractSignalDetailsForTooltipString(x, stringData.values);
    tooltip += '<b> ' + stringValue + '</b>';
    tooltip += '</td>';
    tooltip += '</tr>';

    return tooltip;
  }

  private extractSignalDetailsForTooltipString(x, values) {
    for (const row in values) {
      if (x > values[row][0] && x <= values[row][1]) {
        return values[row][2];
      }
    }
    return null;
  }

  private getSignalPrecision(id, machineSignals: Signal[], chartSignals) {
    const machineSignal = this.getMachineSignalById(id, machineSignals);
    const chartSignal = this.getChartSignalById(id, chartSignals);
    return this.chartsHelperService.getSignalPrecision(machineSignal, chartSignal);
  }

  private getSignalUnit(id, machineSignals, chartSignals) {
    const machineSignal = this.getMachineSignalById(id, machineSignals);
    const chartSignal = this.getChartSignalById(id, chartSignals);

    if (!machineSignal) {
      return '';
    }
    return chartSignal.unit || chartSignal.unit === '' ? chartSignal.unit : machineSignal.unit;
  }

  private getMachineSignalById(id, machineSignals: Signal[]) {
    return Object.values(machineSignals).find(function (item: Signal) {
      return item.table + '@' + item.name === id;
    });

  }

  private getLineWidgetMachineSignalById(id, machineSignals: Signal[]) {
    return Object.values(machineSignals).find(function (item: Signal) {
      return item.id === id;
    });

  }

  private getLineWidgetSignal(id, chartSettings) {
    if (!chartSettings) {
      return undefined;
    }
    if (!chartSettings.axis || chartSettings.axis.length < 1) {
      return null;
    }


    for (const axis of chartSettings.axis) {

      for (const signal of axis.signals) {

        if (id !== -1 && signal.signalId === id) {
          return signal;
        }
      }
    }

    return null;
  }

  private getChartSignalById(id, chartSignals) {
    if (!chartSignals || chartSignals.length === 0) {
      return null;
    }

    return chartSignals.find(function (signal) {
      return signal.signalId === id;
    });
  }
}
